import * as React from 'react'
import { inject, observer } from 'mobx-react'
import { browserHistory } from 'react-router'
import { ICategoryContract, IBaseCategory } from '@getgreenline/homi-shared'
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { DeleteCategoryModal } from './DeleteCategoryModal'
import { MergeCategoryModal } from './MergeCategoryModal'
import './SortableCategoryRow.scss'
import { CategoriesModels } from '@getgreenline/categories'
import { Tooltip } from 'antd'

interface ListElementProps {
  currentCompanyStore?: CurrentCompanyStore
  category: ICategoryContract
  index: number
  canEditCategories: boolean
}

interface State {
  showDeleteModal: boolean
  showMergeModal: IBaseCategory | undefined
  showChildCategories: boolean
}

function swap(array: any[], index1: number, index2: number) {
  const b = array[index1]
  array[index1] = array[index2]
  array[index2] = b
}

@inject('currentCompanyStore')
@observer
export class SortableCategoryRowElement extends React.Component<ListElementProps, State> {
  constructor(props: ListElementProps) {
    super(props)
    this.state = {
      showDeleteModal: false,
      showMergeModal: undefined,
      showChildCategories: false,
    }
  }

  isSystemCategory = () => {
    return this.props.category.categoryType === CategoriesModels.CategoryType.SYSTEM_FEES
  }

  saveReorder() {
    const categories = this.props.currentCompanyStore!.productStore!.categories!

    const categoryIndexMapping = categories.map((category, index) => {
      return {
        categoryId: category.id,
        index: index,
      }
    })

    this.props.currentCompanyStore!.productStore!.updateCategoryOrder(categoryIndexMapping)
  }

  reorderUp() {
    const categories = this.props.currentCompanyStore!.productStore!.categories!
    if (this.props.index > 0) {
      swap(categories, this.props.index, this.props.index - 1)
      this.saveReorder()
    }
  }

  reorderDown() {
    const categories = this.props.currentCompanyStore!.productStore!.categories!
    if (this.props.index < categories.length - 1) {
      swap(categories, this.props.index, this.props.index + 1)
      this.saveReorder()
    }
  }

  showEditCategoryModal = () => {
    if (this.isSystemCategory()) return

    const category = this.props.category
    const addCategoryUrl =
      `${window.location.pathname}/add-category?categoryId=${category.id}`.replace('//', '/')

    browserHistory.push(addCategoryUrl)
  }

  get naturallySortedChildCategories() {
    return this.props.category.childCategories.sort((a, b) => {
      return a.name.localeCompare(b.name, 'en', {
        numeric: true,
        sensitivity: 'base',
      })
    })
  }

  render() {
    const category = this.props.category
    const complianceBadgeStyle =
      category.complianceCategoryId !== null
        ? 'mr-3 badge badge-success'
        : 'mr-3 badge badge-warning'

    const cursor = this.isSystemCategory() ? 'cursor-not-allowed' : 'cursor-pointer'

    const categoryRow = (
      <tr className={`category-row ${cursor}`}>
        <td>
          {this.props.canEditCategories && (
            <>
              <div>
                <span
                  className='fa fa-angle-up cursor-pointer'
                  onClick={() => this.reorderUp()}
                ></span>
              </div>
              <div>
                <span
                  className='fa fa-angle-down cursor-pointer'
                  onClick={() => this.reorderDown()}
                ></span>
              </div>
            </>
          )}
        </td>
        <td onClick={this.showEditCategoryModal}>
          <span
            className='fa fa-square fa-2x ml-1 mr-2'
            style={{ color: category.colorHex ? category.colorHex : 'gray' }}
          />
        </td>
        <td onClick={this.showEditCategoryModal}>
          <div>
            <span className='mr-3 parent-name'>{category.name}</span>
          </div>
        </td>
        <td className='text-muted' onClick={this.showEditCategoryModal}>
          <span className={complianceBadgeStyle}>
            {category.complianceCategoryName || 'Non-cannabis'}
          </span>
        </td>

        <td>
          {this.isSystemCategory() ? (
            <></>
          ) : this.props.canEditCategories ? (
            <UncontrolledDropdown size='sm'>
              <DropdownToggle color='light'>
                <i className='fa fas fa-ellipsis-v' />
              </DropdownToggle>
              <DropdownMenu className='background-shadow'>
                {category.childCategories.length === 0 && (
                  <DropdownItem onClick={() => this.setState({ showMergeModal: category })}>
                    <span>Merge</span>
                  </DropdownItem>
                )}
                <DropdownItem onClick={() => this.setState({ showDeleteModal: true })}>
                  <span className='blaze-text-danger'>Delete</span>
                </DropdownItem>
              </DropdownMenu>
            </UncontrolledDropdown>
          ) : (
            <div>No permissions</div>
          )}
        </td>
      </tr>
    )

    return (
      <>
        {this.isSystemCategory() ? (
          <Tooltip placement='top' title='System categories cannot be edited.'>
            {categoryRow}
          </Tooltip>
        ) : (
          categoryRow
        )}

        {this.naturallySortedChildCategories.map((cc) => {
          return (
            <tr key={cc.id}>
              <td></td>
              <td></td>
              <td>
                <div className='indent-10'>
                  <span className='blaze-text-muted inline'>{cc.name}</span>
                </div>
              </td>
              <td className='text-muted'>
                <span className={complianceBadgeStyle}>
                  {category.complianceCategoryName || 'Non-cannabis'}
                </span>
              </td>

              <td>
                {this.props.canEditCategories && (
                  <UncontrolledDropdown size='sm'>
                    <DropdownToggle color='light'>
                      <i className='fa fas fa-ellipsis-v' />
                    </DropdownToggle>
                    <DropdownMenu className='background-shadow'>
                      <DropdownItem onClick={() => this.setState({ showMergeModal: cc })}>
                        <span>Merge</span>
                      </DropdownItem>
                    </DropdownMenu>
                  </UncontrolledDropdown>
                )}
              </td>
            </tr>
          )
        })}

        {this.state.showDeleteModal && (
          <DeleteCategoryModal
            category={this.props.category}
            onClose={() => this.setState({ showDeleteModal: false })}
          />
        )}
        {this.state.showMergeModal && (
          <MergeCategoryModal
            category={this.state.showMergeModal}
            onClose={() => this.setState({ showMergeModal: undefined })}
          />
        )}
      </>
    )
  }
}
